/**
 * SiteSettingsModal Component - General site settings modal with category navigation
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from 'react-query';
import OnboardingModal from './onboarding/OnboardingModal';
import { 
  X, 
  Settings, 
  Palette, 
  Users, 
  Shield, 
  Bell, 
  Database,
  Zap,
  HelpCircle,
  Save,
  Check,
  Home,
  Clock,
  MapPin,
  Trash2,
  AlertTriangle,
  CreditCard,
  Building2,
  UserPlus,
  Plus,
  Receipt,
  Eye,
  RefreshCw,
  RotateCcw,
  Copy,
  ExternalLink,
  FileCheck,
  CheckCircle2,
  Upload,
  Bot
} from 'lucide-react';
import PaymentMethodManager from './billing/PaymentMethodManager';
import AppearanceSettings from './AppearanceSettings';
import { useAppearanceSettings } from '../contexts/AppearanceSettingsContext';
import HierarchicalPageSelector from './Grid/HierarchicalPageSelector';
import { useMutation } from 'react-query';
import { api } from '../lib/api';
import { useClerk } from '@clerk/clerk-react';
import { handleLogout } from '../lib/logout';
import PDFExportButton from './PDFExportButton';
import toast from 'react-hot-toast';
import UserGroupSelector from './common/UserGroupSelector';
import DocumentImportWizard from './Import/DocumentImportWizard';
import { Select, SelectContent, SelectItem, SelectTrigger } from './ui/Select';
import { showAlert, showConfirm } from './common/AppDialogs';
import AiUsagePanel from './ai/AiUsagePanel';

interface SiteSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Settings categories with icons and descriptions
const settingsCategories = [
  {
    id: 'general',
    name: 'General',
    description: 'Basic site configuration and preferences',
    icon: Settings,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  },
  {
    id: 'appearance',
    name: 'Appearance',
    description: 'Theme, colors, and visual customization',
    icon: Palette,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  {
    id: 'notifications',
    name: 'Notifications',
    description: 'Email alerts, system notifications, and preferences',
    icon: Bell,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  {
    id: 'billing',
    name: 'Billing',
    description: 'Payment methods, invoices, and billing history',
    icon: CreditCard,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  },
  {
    id: 'ai-usage',
    name: 'AI Usage',
    description: 'Monthly AI allocation, usage, and overage policy',
    icon: Bot,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200'
  },
  {
    id: 'data',
    name: 'Data & Storage',
    description: 'Backup, import/export, and data management',
    icon: Database,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200'
  },
  {
    id: 'advanced',
    name: 'Advanced',
    description: 'Developer settings and advanced configuration',
    icon: Zap,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  {
    id: 'approval',
    name: 'Content Approval',
    description: 'Configure approval workflows and default approvers',
    icon: FileCheck,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200'
  }
];

// Internal modal component that uses the context
function SiteSettingsModalContent({ isOpen, onClose }: SiteSettingsModalProps) {
  const [activeCategory, setActiveCategory] = useState('general');
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showJoinOrgModal, setShowJoinOrgModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  
  // State to track save/reset handlers for current category
  const [categoryHandlers, setCategoryHandlers] = useState<{
    hasChanges: boolean;
    onSave: () => void;
    onReset: () => void;
    isLoading?: boolean;
    saveLabel?: string;
  } | null>(null);

  // Clear handlers when category changes
  useEffect(() => {
    setCategoryHandlers(null);
  }, [activeCategory]);

  // Get user profile for permission checks - load when modal opens
  const { data: userProfile } = useQuery(
    ['current-user'], // Use same key as Header component
    () => api.get('/users/me').then(res => res.data.data),
    { enabled: isOpen } // Only fetch when modal is open
  );

  // Get organization staff for permission checks
  const { data: organizationStaff } = useQuery(
    ['organization-staff', userProfile?.organizationId],
    () => api.get(`/organizations/staff?org=${userProfile?.organizationId}`).then(res => res.data.data),
    { enabled: isOpen && !!userProfile?.organizationId }
  );

  // Get organization profile for branding
  const { data: organizationProfile } = useQuery(
    ['organization-profile', userProfile?.organizationId],
    () => api.get(`/organizations/profile/${userProfile?.organizationId}`).then(res => res.data.data),
    { enabled: isOpen && !!userProfile?.organizationId }
  );

  const isOrgAdmin = userProfile?.isAdmin;
  const isNexusOrg = userProfile?.organizationId === 'nexus';

  // Filter categories based on user permissions
  const availableCategories = settingsCategories.filter(category => {
    if (category.id === 'billing') {
      // Only show billing tab to org admins of non-Nexus organizations
      return isOrgAdmin && !isNexusOrg;
    }
    if (category.id === 'approval') {
      // Only show approval tab to admins and head of org
      return isOrgAdmin;
    }
    if (category.id === 'ai-usage') {
      return isOrgAdmin;
    }
    if (category.id === 'data') {
      return isOrgAdmin;
    }
    return true; // Show all other categories
  });

  // Handle organization creation/join completion
  const handleOrganizationComplete = async () => {
    // Refresh user organizations list
    queryClient.invalidateQueries(['user-organizations']);
    queryClient.invalidateQueries(['onboarding-status']);
    queryClient.invalidateQueries(['current-user']);
    
    // Close the modal
    setShowCreateOrgModal(false);
    setShowJoinOrgModal(false);
    
    // Show success message
  };
  
  // Safely get appearance settings with error handling
  let appearanceSettings;
  try {
    appearanceSettings = useAppearanceSettings();
  } catch (error) {
    console.error('AppearanceSettings context not available:', error);
    // Return early or show a loading state if context is not available
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
          <h2 className="text-lg font-semibold mb-4">Loading Settings...</h2>
          <p className="text-muted-foreground">Please wait while settings are being loaded.</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Starting page settings state
  const [startingPageType, setStartingPageType] = useState<'homepage' | 'last_visited' | 'specific_page'>('homepage');
  const [selectedStartingPageId, setSelectedStartingPageId] = useState<string>('');
  const [hasStartingPageChanges, setHasStartingPageChanges] = useState(false);
  const [isLoadingStartingPageSettings, setIsLoadingStartingPageSettings] = useState(false);
  const [showPageSelector, setShowPageSelector] = useState(false);

  // Fetch all pages for page selection
  const { data: allPages, error: pagesError, isLoading: pagesLoading } = useQuery(
    'all-pages',
      () => api.get('/pages').then(res => res.data.data),
    { enabled: isOpen }
  );

  // Fetch homepage for default option
  const { data: homepage } = useQuery(
    'homepage-settings',
    () => api.get('/pages/homepage').then(res => res.data.data),
    { enabled: isOpen }
  );

  // Load starting page settings when modal opens
  useEffect(() => {
    if (isOpen) {
      loadStartingPageSettings();
    }
  }, [isOpen]);

  const loadStartingPageSettings = async () => {
    try {
      const response = await api.get('/settings/user/starting_page');
      const settings = response.data.settingValue;
      
      setStartingPageType(settings.startingPageType || 'homepage');
      setSelectedStartingPageId(settings.selectedStartingPageId || '');
      setHasStartingPageChanges(false);
      
      if (settings.startingPageType === 'specific_page') {
        setShowPageSelector(true);
      }
    } catch (error: any) {
      // If no settings exist yet, use defaults (already set in state)
      if (error?.response?.status === 404) {
        console.debug('No starting page settings found, using defaults');
      } else {
        console.error('Error loading starting page settings:', error);
      }
    }
  };

  const activeCategoryData = settingsCategories.find(cat => cat.id === activeCategory);

  // Handle close with unsaved changes check
  const handleClose = async () => {
    const hasAnyUnsavedChanges = appearanceSettings.hasUnsavedChanges || hasStartingPageChanges;
    if (hasAnyUnsavedChanges) {
      const shouldSave = await showConfirm('You have unsaved settings. Would you like to save them before closing?');
      if (shouldSave) {
        handleSaveAndClose();
        return;
      }
    }
    onClose();
  };

  // Handle starting page type change
  const handleStartingPageTypeChange = (type: 'homepage' | 'last_visited' | 'specific_page') => {
    setStartingPageType(type);
    setHasStartingPageChanges(true);
    
    if (type === 'specific_page') {
      setShowPageSelector(true);
    } else {
      setShowPageSelector(false);
      setSelectedStartingPageId('');
    }
  };

  // Handle page selection for specific page option
  const handlePageSelect = (pageId: string) => {
    setSelectedStartingPageId(pageId);
    setHasStartingPageChanges(true);
  };

  // Save starting page settings
  const saveStartingPageSettings = async () => {
    if (!hasStartingPageChanges) return;

    // Validate that specific page option has a selected page
    if (startingPageType === 'specific_page' && !selectedStartingPageId) {
      throw new Error('Please select a page before saving.');
    }

    setIsLoadingStartingPageSettings(true);
    try {
      const settingsData = {
        startingPageType,
        selectedStartingPageId: startingPageType === 'specific_page' ? selectedStartingPageId : null
      };

      await api.put('/settings/user/starting_page', settingsData);
      setHasStartingPageChanges(false);
    } catch (error) {
      console.error('Failed to save starting page settings:', error);
      throw error;
    } finally {
      setIsLoadingStartingPageSettings(false);
    }
  };

  // Save settings and keep modal open
  const handleSave = async () => {
    try {
      const savePromises = [];
      
      // Only save appearance settings if there are changes
      if (appearanceSettings.hasUnsavedChanges) {
        savePromises.push(appearanceSettings.saveSettings());
      }
      
      // Only save starting page settings if there are changes
      if (hasStartingPageChanges) {
        savePromises.push(saveStartingPageSettings());
      }
      
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }
      
      showAlert('Settings saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      showAlert('Failed to save settings. Please try again.');
    }
  };

  // Save settings and close modal
  const handleSaveAndClose = async () => {
    try {
      const savePromises = [];
      
      // Only save appearance settings if there are changes
      if (appearanceSettings.hasUnsavedChanges) {
        savePromises.push(appearanceSettings.saveSettings());
      }
      
      // Only save starting page settings if there are changes
      if (hasStartingPageChanges) {
        savePromises.push(saveStartingPageSettings());
      }
      
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }
      
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      showAlert('Failed to save settings. Please try again.');
    }
  };

  // Close modal when clicking outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle escape key and body scroll
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderCategoryContent = () => {
    switch (activeCategory) {
      case 'general':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">General Settings</h3>
              
              {/* Starting Page Settings */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Home className="h-5 w-5 text-blue-600" />
                  <div>
                    <h4 className="font-semibold text-foreground">Starting Page</h4>
                    <p className="text-sm text-muted-foreground">Choose which page to show when you open Nexus</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Option 1: Organization Homepage */}
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id="homepage"
                      name="startingPage"
                      value="homepage"
                      checked={startingPageType === 'homepage'}
                      onChange={() => handleStartingPageTypeChange('homepage')}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="flex-1">
                      <label htmlFor="homepage" className="flex items-center space-x-2 cursor-pointer">
                        <Home className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-foreground">Organization Homepage</span>
                      </label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Always start at your organization's homepage (default)
                      </p>
                      {homepage && (
                        <p className="text-xs text-gray-500 mt-1">
                          Current homepage: {homepage.title}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Option 2: Last Visited Page */}
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id="last_visited"
                      name="startingPage"
                      value="last_visited"
                      checked={startingPageType === 'last_visited'}
                      onChange={() => handleStartingPageTypeChange('last_visited')}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="flex-1">
                      <label htmlFor="last_visited" className="flex items-center space-x-2 cursor-pointer">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-foreground">Last Page Visited</span>
                      </label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Resume where you left off in your previous session
                      </p>
                    </div>
                  </div>

                  {/* Option 3: Specific Page */}
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id="specific_page"
                      name="startingPage"
                      value="specific_page"
                      checked={startingPageType === 'specific_page'}
                      onChange={() => handleStartingPageTypeChange('specific_page')}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="flex-1">
                      <label htmlFor="specific_page" className="flex items-center space-x-2 cursor-pointer">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-foreground">Specific Page</span>
                      </label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Always start at a specific page you choose
                      </p>
                      
                      {/* Page Selection */}
                      {startingPageType === 'specific_page' && (
                        <div className="mt-3 space-y-3">
                          {!selectedStartingPageId ? (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                              <p className="text-sm text-amber-800 font-medium">Page selection required</p>
                              <p className="text-xs text-amber-700 mt-1">
                                You must select a page before saving these settings.
                              </p>
                            </div>
                          ) : (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                              <p className="text-sm text-green-800 font-medium">Selected page:</p>
                              <p className="text-xs text-green-700 mt-1">
                                {allPages?.find((p: any) => p.id === selectedStartingPageId)?.title || 'Loading...'}
                              </p>
                            </div>
                          )}
                          
                          {showPageSelector && allPages && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Choose your starting page:</label>
                                <HierarchicalPageSelector
                                  pages={allPages}
                                  selectedPageId={selectedStartingPageId}
                                  currentPageId="" // No current page restriction for settings
                                  onSelectPage={handlePageSelect}
                                />
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fallback Information */}
                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                  <div className="flex items-start space-x-2">
                    <HelpCircle className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Fallback Behavior</p>
                      <p className="text-xs text-gray-600 mt-1">
                        If your selected starting page is deleted, the setting will automatically revert to "Organization Homepage" to ensure you can always access Nexus.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delete Account Section */}
              <DeleteAccountSection onClose={onClose} />
            </div>

            {/* Nexus Guide shortcut restore */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-3">
                <HelpCircle className="h-5 w-5 text-amber-600" />
                <div>
                  <h4 className="font-semibold text-amber-900">Welcome to Nexus shortcut</h4>
                  <p className="text-sm text-amber-800">Restore the shared read-only guide tile on your homepage.</p>
                </div>
              </div>
              <button
                  onClick={async () => {
                    try {
                      const res = await api.post('/organizations/restore-welcome-shortcut', {});
                      if (res.data?.success) {
                        if (res.data.data?.created) {
                          toast.success('Shortcut created on homepage.');
                        } else {
                          toast('Shortcut already exists.');
                        }
                        // Invalidate homepage query to refresh the page
                        queryClient.invalidateQueries(['page']);
                        queryClient.invalidateQueries(['pages']);
                        // Force a page reload to show the new tile immediately
                        setTimeout(() => window.location.reload(), 500);
                      } else {
                        toast.error('Failed to restore shortcut');
                      }
                    } catch (e: any) {
                      console.error('Restore shortcut failed', e);
                      toast.error(e?.response?.data?.error || 'Failed to restore shortcut');
                    }
                  }}
                className="inline-flex items-center px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
              >
                Restore shortcut
              </button>
            </div>

            {/* Apply Nexus Template */}
            <ApplyTemplateSection queryClient={queryClient} />
          </div>
        );

      case 'appearance':
        return <AppearanceSettings modalRef={modalRef} />;

      case 'notifications':
        return <NotificationSettings onRegisterHandlers={setCategoryHandlers} />;

      case 'billing':
        return <BillingSettings userProfile={userProfile} isModalOpen={isOpen} />;

      case 'ai-usage':
        return <AiUsagePanel canEditPolicy={!isNexusOrg} />;

      case 'data':
        return <DataStorageSettings userProfile={userProfile} onRegisterHandlers={setCategoryHandlers} />;

      case 'approval':
        return <ContentApprovalSettings userProfile={userProfile} onRegisterHandlers={setCategoryHandlers} />;

      case 'advanced':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Advanced Settings</h3>
              
              {/* PDF Export Button - Only visible to admins and heads of organization */}
              <PDFExportButton
                currentUser={userProfile}
                organizationStaff={organizationStaff || []}
                organizationName={organizationProfile?.companyName || 'Organization'}
                organizationId={userProfile?.organizationId}
                className="mb-4"
              />
              
              {/* Organization Management Section */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Building2 className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Organization Management</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Create New Organization Button */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Plus className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-blue-800 font-medium mb-2">Create New Organization</h4>
                        <p className="text-blue-700 text-sm mb-2">
                          Start a new organization with its own billing, team, and content.
                        </p>
                        <p className="text-blue-600 text-xs mb-3 font-medium">
                          Note: Each organization is billed separately, even if members overlap with your current organization.
                        </p>
                        <button
                          onClick={() => setShowCreateOrgModal(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2"
                        >
                          <Plus className="h-4 w-4" />
                          <span>Create Organization</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Join Another Organization Button */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <UserPlus className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-green-800 font-medium mb-2">Join Another Organization</h4>
                        <p className="text-green-700 text-sm mb-2">
                          Request to join an existing organization using a joining code.
                        </p>
                        <p className="text-green-600 text-xs mb-3 font-medium">
                          Note: Both organizations will be billed if approved.
                        </p>
                        <button
                          onClick={() => setShowJoinOrgModal(true)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2"
                        >
                          <UserPlus className="h-4 w-4" />
                          <span>Join Organization</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">Select a settings category to continue.</p>
          </div>
        );
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="bg-card rounded-lg shadow-xl w-full max-w-7xl h-[80vh] flex overflow-hidden relative border border-border"
      >
        {/* Sidebar Navigation */}
        <div className="w-80 bg-muted border-r border-border flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Settings className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Site Settings</h2>
                  <p className="text-sm text-muted-foreground">Configure your Nexus site</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Categories */}
          <div className="flex-1 overflow-y-auto p-4">
            <nav className="space-y-2">
              {availableCategories.map((category) => {
                const IconComponent = category.icon;
                const isActive = activeCategory === category.id;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                      isActive
                        ? `${category.bgColor} ${category.borderColor} border-2 ${category.color}`
                        : 'hover:bg-muted-foreground/10 border-2 border-transparent text-muted-foreground'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <IconComponent className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                        isActive ? category.color : 'text-muted-foreground/60'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm ${
                          isActive ? category.color : 'text-foreground'
                        }`}>
                          {category.name}
                        </p>
                        <p className={`text-xs mt-1 leading-relaxed ${
                          isActive ? category.color.replace('600', '700') : 'text-muted-foreground/80'
                        }`}>
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Content Header */}
          <div className="p-6 border-b border-border bg-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {activeCategoryData && (
                  <>
                    <div className={`p-2 ${activeCategoryData.bgColor} rounded-lg`}>
                      <activeCategoryData.icon className={`h-5 w-5 ${activeCategoryData.color}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-card-foreground">
                        {activeCategoryData.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {activeCategoryData.description}
                      </p>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content Body */}
          <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
            {renderCategoryContent()}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border bg-card">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Nexus Site Settings
              </div>
              <div className="flex items-center space-x-3">
                {/* Show category-specific buttons if registered */}
                {categoryHandlers && categoryHandlers.hasChanges ? (
                  <>
                    <button
                      onClick={categoryHandlers.onReset}
                      disabled={categoryHandlers.isLoading}
                      className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reset
                    </button>
                    <button
                      onClick={categoryHandlers.onSave}
                      disabled={categoryHandlers.isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-violet-600 rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <Save className="h-4 w-4" />
                      <span>{categoryHandlers.isLoading ? 'Saving...' : (categoryHandlers.saveLabel || 'Save Settings')}</span>
                    </button>
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-muted transition-colors"
                    >
                      Close
                    </button>
                  </>
                ) : (appearanceSettings.hasUnsavedChanges || hasStartingPageChanges) ? (
                  <>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-muted transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={appearanceSettings.isLoading || isLoadingStartingPageSettings}
                      className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <Save className="h-4 w-4" />
                      <span>{(appearanceSettings.isLoading || isLoadingStartingPageSettings) ? 'Saving...' : 'Save'}</span>
                    </button>
                    <button
                      onClick={handleSaveAndClose}
                      disabled={appearanceSettings.isLoading || isLoadingStartingPageSettings}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <Check className="h-4 w-4" />
                      <span>{(appearanceSettings.isLoading || isLoadingStartingPageSettings) ? 'Saving...' : 'Save & Close'}</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render modal at document body level
  return createPortal(
    <>
      {modalContent}
      
      {/* Organization Management Modals */}
      <OnboardingModal
        key="create-org-modal"
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        onComplete={handleOrganizationComplete}
        isAdditionalOrganization={true}
        directPath="create_org"
      />
      
      <OnboardingModal
        key="join-org-modal"
        isOpen={showJoinOrgModal}
        onClose={() => setShowJoinOrgModal(false)}
        onComplete={handleOrganizationComplete}
        isAdditionalOrganization={true}
        directPath="join_org"
      />
    </>,
    document.body
  );
}

// Billing Settings Component
function BillingSettings({ userProfile, isModalOpen }: { userProfile?: any; isModalOpen: boolean }) {
  const [isLoading, setIsLoading] = useState(true);
  const [discountInfo, setDiscountInfo] = useState<any>(null);
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [showReplaceCodeModal, setShowReplaceCodeModal] = useState(false);
  const [showBilledUsersModal, setShowBilledUsersModal] = useState(false);
  const [selectedBillingRecord, setSelectedBillingRecord] = useState<any>(null);
  const [billedUsers, setBilledUsers] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [newDiscountCode, setNewDiscountCode] = useState('');
  const [isReplacingCode, setIsReplacingCode] = useState(false);

  const isOrgAdmin = userProfile?.isAdmin;
  const isNexusOrg = userProfile?.organizationId === 'nexus';

  // Load billing data when modal opens and user profile is available
  useEffect(() => {
    if (isModalOpen && userProfile) {
      if (isOrgAdmin && !isNexusOrg) {
        loadBillingData();
      } else {
        setIsLoading(false); // Not an admin or Nexus org, no billing access
      }
    }
  }, [isModalOpen, userProfile, isOrgAdmin, isNexusOrg]);

  const loadBillingData = async () => {
    try {
      setIsLoading(true);
      
      console.log('🏦 Loading billing data...');
      const startTime = Date.now();
      
      // Load discount info and billing history in parallel with timeout
      const [discountResponse, historyResponse] = await Promise.all([
        Promise.race([
          api.get('/organizations/billing/discount'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Discount API timeout')), 10000))
        ]).catch((error) => {
          console.warn('Failed to load discount info:', error.message);
          return { data: { data: null } };
        }),
        Promise.race([
          api.get('/organizations/billing/history'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('History API timeout')), 10000))
        ]).catch((error) => {
          console.warn('Failed to load billing history:', error.message);
          return { data: { data: [] } };
        })
      ]);

      const loadTime = Date.now() - startTime;
      console.log(`🏦 Billing data loaded in ${loadTime}ms`);

      setDiscountInfo((discountResponse as any).data.data);
      
      // Ensure billingHistory is always an array
      const historyData = (historyResponse as any).data.data;
      if (Array.isArray(historyData)) {
        setBillingHistory(historyData);
      } else {
        console.error('🚨 Billing history data is not an array:', historyData);
        setBillingHistory([]);
      }
      
      setHasMoreHistory(false); // New endpoint returns all records (12 max)
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreHistory = async () => {
    try {
      const response = await api.get(`/organizations/billing/history?page=${currentPage + 1}`);
      const newRecords = response.data.data.records;
      
      if (Array.isArray(newRecords)) {
        setBillingHistory(prev => [...prev, ...newRecords]);
      } else {
        console.error('🚨 Additional billing history data is not an array:', newRecords);
      }
      
      setCurrentPage(prev => prev + 1);
      setHasMoreHistory(response.data.data.pagination.hasMore);
    } catch (error) {
      console.error('Error loading more history:', error);
    }
  };

  const handleReplaceCode = async () => {
    if (!newDiscountCode.trim()) return;
    
    try {
      setIsReplacingCode(true);
      await api.post('/organizations/billing/discount/replace', { newCode: newDiscountCode.trim() });
      setShowReplaceCodeModal(false);
      setNewDiscountCode('');
      await loadBillingData(); // Refresh data
      showAlert('Discount code updated successfully!');
    } catch (error: any) {
      showAlert(error.response?.data?.error || 'Failed to update discount code');
    } finally {
      setIsReplacingCode(false);
    }
  };

  const handleViewBilledUsers = async (billingRecord: any) => {
    try {
      setSelectedBillingRecord(billingRecord);
      const response = await api.get(`/organizations/billing/users/${billingRecord.id}`);
      setBilledUsers(response.data.data.billedUsers || []);
      setShowBilledUsersModal(true);
    } catch (error) {
      console.error('Error loading billed users:', error);
      showAlert('Failed to load billed users');
    }
  };

  const handleViewStripeInvoice = async (billingRecord: any) => {
    try {
      const response = await api.get(`/organizations/billing/invoice/${billingRecord.id}`);
      const { hasStripeInvoice, stripeInvoice } = response.data.data;
      
      if (hasStripeInvoice && stripeInvoice?.hosted_invoice_url) {
        // Open Stripe hosted invoice page in new tab
        window.open(stripeInvoice.hosted_invoice_url, '_blank');
      } else {
        showAlert('No Stripe invoice available for this billing record');
      }
    } catch (error) {
      console.error('Error accessing Stripe invoice:', error);
      showAlert('Failed to access Stripe invoice');
    }
  };

  const handleDownloadStripeInvoice = async (billingRecord: any) => {
    try {
      const response = await api.get(`/organizations/billing/invoice/${billingRecord.id}`);
      const { hasStripeInvoice, stripeInvoice } = response.data.data;
      
      if (hasStripeInvoice && stripeInvoice?.invoice_pdf) {
        // Open Stripe PDF invoice in new tab
        window.open(stripeInvoice.invoice_pdf, '_blank');
      } else {
        showAlert('No PDF invoice available for this billing record');
      }
    } catch (error) {
      console.error('Error downloading Stripe invoice:', error);
      showAlert('Failed to download Stripe invoice');
    }
  };

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(parseFloat(amount.toString()));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      paid: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      invoiced: 'bg-blue-100 text-blue-800',
      overdue: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors] || statusColors.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isNexusOrg) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Billing Settings</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CreditCard className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-blue-800 font-medium">Nexus Organization</p>
                <p className="text-blue-700 text-sm mt-1">
                  The Nexus organization is not subject to billing. This section is only available for customer organizations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOrgAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Billing Settings</h3>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-amber-800 font-medium">Organization Administrator Required</p>
                <p className="text-amber-700 text-sm mt-1">
                  Only organization administrators can access billing settings and payment information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Billing Settings</h3>
          <div className="text-sm text-muted-foreground">Loading billing information...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Billing Settings</h3>
        
        {/* Current Discount Code Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Receipt className="h-5 w-5 text-emerald-600" />
              <div>
                <h4 className="font-semibold text-foreground">Current Discount Code</h4>
                <p className="text-sm text-muted-foreground">
                  Manage your organization's discount code
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowReplaceCodeModal(true)}
              className="px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-md hover:bg-emerald-100 transition-colors"
            >
              Replace Code
            </button>
          </div>

          {discountInfo ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-500">Code:</span>
                <span className="px-2 py-1 bg-gray-100 rounded font-mono text-sm">{discountInfo.code}</span>
                <button
                  onClick={() => navigator.clipboard.writeText(discountInfo.code)}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Copy code"
                >
                  <Copy className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Type:</span>
                  <span className="ml-2 capitalize">{discountInfo.discountType.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Value:</span>
                  <span className="ml-2">
                    {discountInfo.discountType === 'percentage' 
                      ? `${discountInfo.discountValue}%` 
                      : formatCurrency(discountInfo.discountValue)}
                  </span>
                </div>
                {discountInfo.userLimit && (
                  <div>
                    <span className="font-medium text-gray-500">User Limit:</span>
                    <span className="ml-2">{discountInfo.userLimit} users</span>
                  </div>
                )}
                {discountInfo.billingPeriodLimit && (
                  <div>
                    <span className="font-medium text-gray-500">Period Limit:</span>
                    <span className="ml-2">{discountInfo.periodsUsed}/{discountInfo.billingPeriodLimit} used</span>
                  </div>
                )}
                {discountInfo.validUntil && (
                  <div>
                    <span className="font-medium text-gray-500">Expires:</span>
                    <span className="ml-2">{new Date(discountInfo.validUntil).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {discountInfo.description && (
                <p className="text-sm text-gray-600 italic">{discountInfo.description}</p>
              )}

              {(!discountInfo.canUse) && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800 font-medium">
                    {discountInfo.isExpired ? 'Discount code has expired' : 'Discount code usage limit reached'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500">No discount code currently applied</p>
            </div>
          )}
        </div>

        {/* Payment Method Section */}
        {userProfile && !isLoading && (
          <PaymentMethodManager 
            organizationId={userProfile?.organizationId} 
            isNexusOrg={isNexusOrg}
          />
        )}

        {/* Billing History Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Receipt className="h-5 w-5 text-emerald-600" />
            <div>
              <h4 className="font-semibold text-foreground">Billing History</h4>
              <p className="text-sm text-muted-foreground">View your past invoices and billing records</p>
            </div>
          </div>

          {billingHistory.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No billing history available</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Billing Period
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billingHistory.map((record, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(record.billingMonth)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.userCount} users
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{formatCurrency(record.finalAmount)}</div>
                            {parseFloat(record.discountAmount) > 0 && (
                              <div className="text-xs text-green-600">
                                -{formatCurrency(record.discountAmount)} discount
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getStatusBadge(record.status)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col space-y-1">
                            {/* View Users Button */}
                            <button
                              onClick={() => handleViewBilledUsers(record)}
                              className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View Users
                            </button>
                            
                            {/* Stripe Invoice Buttons */}
                            {record.hasStripeInvoice ? (
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleViewStripeInvoice(record)}
                                  className="inline-flex items-center px-2 py-1 border border-blue-300 rounded text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  View Invoice
                                </button>
                                <button
                                  onClick={() => handleDownloadStripeInvoice(record)}
                                  className="inline-flex items-center px-1 py-1 border border-green-300 rounded text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100"
                                  title="Download PDF"
                                >
                                  <Receipt className="h-3 w-3" />
                                </button>
                              </div>
                            ) : (
                              <button
                                disabled
                                className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-400 bg-gray-50 cursor-not-allowed"
                                title="No Stripe invoice available"
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                No Invoice
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMoreHistory && (
                <div className="text-center">
                  <button
                    onClick={loadMoreHistory}
                    className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-md hover:bg-emerald-100 transition-colors"
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Replace Discount Code Modal */}
      {showReplaceCodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Replace Discount Code</h3>
              <button
                onClick={() => setShowReplaceCodeModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Discount Code
                </label>
                <input
                  type="text"
                  value={newDiscountCode}
                  onChange={(e) => setNewDiscountCode(e.target.value.toUpperCase())}
                  placeholder="Enter new discount code"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowReplaceCodeModal(false)}
                  disabled={isReplacingCode}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReplaceCode}
                  disabled={isReplacingCode || !newDiscountCode.trim()}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isReplacingCode ? 'Updating...' : 'Update Code'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billed Users Modal */}
      {showBilledUsersModal && selectedBillingRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Billed Users - {formatDate(selectedBillingRecord.billingMonth)}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {billedUsers.length} users billed for this period
                </p>
              </div>
              <button
                onClick={() => setShowBilledUsersModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {billedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No billed users found for this period</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Active Days
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Periods
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {billedUsers.map((user, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{user.totalActiveDays} days</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-xs text-gray-500">
                              {user.organizationStaffRecords.length} period(s)
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Notification Settings Component
function NotificationSettings({ onRegisterHandlers }: { onRegisterHandlers?: (handlers: any) => void }) {
  const [preferences, setPreferences] = useState({
    inAppBadge: true,
    inAppPopup: false,
    emailNotifications: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current notification preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await api.get('/settings/notifications');
        if (response.data && response.data.data) {
          setPreferences(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch notification preferences:', error);
        // Keep default values if fetch fails
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, []);

  const handlePreferenceChange = (key: keyof typeof preferences, value: boolean) => {
    console.log('🔧 Notification preference change:', { key, value, currentPreferences: preferences });
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await api.put('/settings/notifications', preferences);
      setHasChanges(false);
      showAlert('Notification preferences saved successfully!');
    } catch (error) {
      console.error('❌ Failed to save notification preferences:', error);
      showAlert('Failed to save notification preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [preferences]);

  const handleReset = useCallback(() => {
    // Reset to last saved state by refetching
    setIsLoading(true);
    const fetchPreferences = async () => {
      try {
        const response = await api.get('/settings/notifications');
        if (response.data && response.data.data) {
          setPreferences(response.data.data);
        } else {
          // Reset to defaults if no data
          setPreferences({
            inAppBadge: true,
            inAppPopup: false,
            emailNotifications: true
          });
        }
        setHasChanges(false);
      } catch (error) {
        console.error('Failed to fetch notification preferences:', error);
        // Reset to defaults on error
        setPreferences({
          inAppBadge: true,
          inAppPopup: false,
          emailNotifications: true
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  // Store handlers in refs to avoid dependency issues
  const handleSaveRef = useRef(handleSave);
  const handleResetRef = useRef(handleReset);
  const prevValuesRef = useRef<{ hasChanges: boolean; isLoading: boolean } | null>(null);

  // Update refs when handlers change
  useEffect(() => {
    handleSaveRef.current = handleSave;
    handleResetRef.current = handleReset;
  }, [handleSave, handleReset]);

  // Register handlers with parent - only update when actual values change
  useEffect(() => {
    if (onRegisterHandlers && (
      prevValuesRef.current === null ||
      prevValuesRef.current.hasChanges !== hasChanges ||
      prevValuesRef.current.isLoading !== isSaving
    )) {
      prevValuesRef.current = { hasChanges, isLoading: isSaving };
      onRegisterHandlers({
        hasChanges,
        onSave: handleSaveRef.current,
        onReset: handleResetRef.current,
        isLoading: isSaving,
        saveLabel: 'Save Preferences'
      });
    }
  }, [hasChanges, isSaving, onRegisterHandlers]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Notification Settings</h3>
          <div className="text-sm text-muted-foreground">Loading notification preferences...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Notification Settings</h3>
        
        {/* Notification Preferences */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Bell className="h-5 w-5 text-yellow-600" />
            <div>
              <h4 className="font-semibold text-foreground">Notification Preferences</h4>
              <p className="text-sm text-muted-foreground">
                Choose how you want to receive notifications
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* In-app Badge (Always Active) */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Bell className="h-4 w-4 text-gray-500" />
                  <h5 className="font-medium text-foreground">In-app indicator on User Menu</h5>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Red notification badge showing unread count (always active)
                </p>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-green-600 font-medium">Always Active</span>
              </div>
            </div>

            {/* In-app Popup */}
            <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Bell className="h-4 w-4 text-gray-500" />
                  <h5 className="font-medium text-foreground">In-app popup</h5>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Show popup notifications while using the app (coming soon)
                </p>
              </div>
              <div className="flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences?.inAppPopup || false}
                    onChange={(e) => handlePreferenceChange('inAppPopup', e.target.checked)}
                    disabled={true} // Disabled since it's not implemented yet
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 opacity-50`}></div>
                </label>
                <span className="ml-2 text-sm text-gray-400">Coming Soon</span>
              </div>
            </div>

            {/* Email Notifications */}
            <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Bell className="h-4 w-4 text-gray-500" />
                  <h5 className="font-medium text-foreground">Email notification</h5>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Receive notifications via email
                </p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => handlePreferenceChange('emailNotifications', !preferences?.emailNotifications)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    preferences?.emailNotifications ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences?.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

        </div>

        {/* Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Bell className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-800 font-medium">Notification Types</p>
              <p className="text-blue-700 text-sm mt-1">
                These settings apply to all notification types including join requests, 
                approvals, system alerts, and other organizational notifications.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Content Approval Settings Component
function ContentApprovalSettings({ userProfile, onRegisterHandlers }: { userProfile?: any; onRegisterHandlers?: (handlers: any) => void }) {
  const queryClient = useQueryClient();
  const organizationId = userProfile?.organizationId;
  
  // Load approval defaults
  const { data: approvalDefaults, isLoading } = useQuery(
    ['organization-settings', organizationId, 'content_approval_defaults'],
    () => api.get(`/settings/approval/defaults`).then(res => res.data.data),
    {
      enabled: !!organizationId,
      onError: () => {
        // Use defaults if not found
      }
    }
  );

  const [settings, setSettings] = useState<any>({
    enabled: {
      content: false,
      roadmap: false,
      form: false,
      pages: false,
      workflows: false
    },
    defaultApprover: {
      type: 'admins',
      value: null
    },
    approverChangeBehavior: {
      autoApprove: false,
      deferTo: 'admins'
    },
    // defaultPageChangesRequiringApproval removed - page approval removed
    tileApproverOnMove: 'change_to_default',
    pageDefaultsInheritFromParent: false
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize settings from API - only on first load
  useEffect(() => {
    if (approvalDefaults && !isInitialized) {
      setSettings(approvalDefaults);
      setHasChanges(false);
      setIsInitialized(true);
    }
  }, [approvalDefaults, isInitialized]);

  // Save mutation
  const saveMutation = useMutation(
    (newSettings: any) => api.put('/settings/approval/defaults', { settingValue: newSettings }),
    {
      onSuccess: (response, variables) => {
        setHasChanges(false);
        // Update the query cache with the new settings immediately
        // This prevents the useEffect from resetting settings to stale data when the query refetches
        queryClient.setQueryData(
          ['organization-settings', organizationId, 'content_approval_defaults'],
          variables
        );
        // Invalidate to ensure other components get the update, but the cache is already updated
        // so the useEffect won't reset to stale data
        queryClient.invalidateQueries(['organization-settings', organizationId, 'content_approval_defaults']);
        toast.success('Approval defaults saved successfully!');
      },
      onError: (error: any) => {
        toast.error(`Failed to save: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const handleSave = useCallback(() => {
    saveMutation.mutate(settings);
  }, [saveMutation, settings]);

  const handleReset = useCallback(() => {
    if (approvalDefaults) {
      setSettings(approvalDefaults);
      setHasChanges(false);
    }
  }, [approvalDefaults]);

  // Store handlers in refs to avoid dependency issues
  const handleSaveRef = useRef(handleSave);
  const handleResetRef = useRef(handleReset);
  const prevValuesRef = useRef<{ hasChanges: boolean; isLoading: boolean } | null>(null);

  // Update refs when handlers change
  useEffect(() => {
    handleSaveRef.current = handleSave;
    handleResetRef.current = handleReset;
  }, [handleSave, handleReset]);

  // Register handlers with parent - only update when actual values change
  useEffect(() => {
    if (onRegisterHandlers && (
      prevValuesRef.current === null ||
      prevValuesRef.current.hasChanges !== hasChanges ||
      prevValuesRef.current.isLoading !== saveMutation.isLoading
    )) {
      prevValuesRef.current = { hasChanges, isLoading: saveMutation.isLoading };
      onRegisterHandlers({
        hasChanges,
        onSave: handleSaveRef.current,
        onReset: handleResetRef.current,
        isLoading: saveMutation.isLoading,
        saveLabel: 'Save Settings'
      });
    }
  }, [hasChanges, saveMutation.isLoading, onRegisterHandlers]);

  const updateSetting = (path: string[], value: any) => {
    setSettings((prev: any) => {
      const newSettings = { ...prev };
      let current = newSettings;
      for (let i = 0; i < path.length - 1; i++) {
        current[path[i]] = { ...current[path[i]] };
        current = current[path[i]];
      }
      current[path[path.length - 1]] = value;
      setHasChanges(true);
      return newSettings;
    });
  };

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  useEffect(() => {
    if (settings.defaultApprover?.type === 'specific' && settings.defaultApprover?.value) {
      if (settings.defaultApprover.valueType === 'user') {
        setSelectedUsers([settings.defaultApprover.value]);
        setSelectedGroups([]);
      } else if (settings.defaultApprover.valueType === 'group') {
        setSelectedGroups([settings.defaultApprover.value]);
        setSelectedUsers([]);
      }
    } else {
      setSelectedUsers([]);
      setSelectedGroups([]);
    }
  }, [settings.defaultApprover]);

  if (isLoading) {
    return <div className="p-6 text-center text-muted-foreground">Loading approval settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Content Approval Defaults</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Configure default approval settings for content, forms, roadmaps, and pages. These settings apply to new items unless overridden at the tile or page level.
        </p>

        {/* Enable Approval by Type */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-foreground mb-4">Enable Approval By Type</h4>
          <div className="space-y-3">
            {['content', 'roadmap', 'form', 'workflows'].map((type) => (
              <div key={type} className="flex items-center justify-between">
                <label className="text-sm text-foreground capitalize">
                  {type === 'pages' ? 'Pages' : type === 'workflows' ? 'Workflows (Coming Soon)' : `${type} Tiles`}
                </label>
                <button
                  onClick={() => updateSetting(['enabled', type], !settings.enabled[type])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.enabled[type] ? 'bg-violet-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.enabled[type] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Default Approver */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-foreground mb-4">Default Approver</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-foreground mb-2">Approver Type</label>
              <select
                value={settings.defaultApprover?.type || 'admins'}
                onChange={(e) => {
                  const type = e.target.value;
                  updateSetting(['defaultApprover', 'type'], type);
                  if (type !== 'specific') {
                    updateSetting(['defaultApprover', 'value'], null);
                    updateSetting(['defaultApprover', 'valueType'], null);
                  }
                }}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="tile_creator">Tile Creator</option>
                <option value="parent_page_approver">Parent Page Approver</option>
                <option value="admins">Admins Group</option>
                <option value="head_of_org">Head of Organisation</option>
                <option value="specific">Specific Person or Group</option>
              </select>
            </div>

            {settings.defaultApprover?.type === 'specific' && (
              <div>
                <label className="block text-sm text-foreground mb-2">Select Approver</label>
                <UserGroupSelector
                  selectedUsers={selectedUsers}
                  selectedGroups={selectedGroups}
                  onAddUser={(userId) => {
                    setSelectedUsers([userId]);
                    setSelectedGroups([]);
                    updateSetting(['defaultApprover', 'value'], userId);
                    updateSetting(['defaultApprover', 'valueType'], 'user');
                  }}
                  onAddGroup={(groupId) => {
                    setSelectedGroups([groupId]);
                    setSelectedUsers([]);
                    updateSetting(['defaultApprover', 'value'], groupId);
                    updateSetting(['defaultApprover', 'valueType'], 'group');
                  }}
                  onRemoveUser={() => {
                    setSelectedUsers([]);
                    updateSetting(['defaultApprover', 'value'], null);
                    updateSetting(['defaultApprover', 'valueType'], null);
                  }}
                  onRemoveGroup={() => {
                    setSelectedGroups([]);
                    updateSetting(['defaultApprover', 'value'], null);
                    updateSetting(['defaultApprover', 'valueType'], null);
                  }}
                  hideSelected={false}
                />
              </div>
            )}
          </div>
        </div>

        {/* Approver Change Behavior */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-foreground mb-4">When Approver Makes Changes</h4>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="autoApprove"
                name="approverBehavior"
                checked={settings.approverChangeBehavior?.autoApprove === true}
                onChange={() => updateSetting(['approverChangeBehavior', 'autoApprove'], true)}
                className="h-4 w-4 text-violet-600"
              />
              <label htmlFor="autoApprove" className="text-sm text-foreground">
                Automatically approved (no approval flow)
              </label>
            </div>
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                id="deferApproval"
                name="approverBehavior"
                checked={settings.approverChangeBehavior?.autoApprove === false}
                onChange={() => updateSetting(['approverChangeBehavior', 'autoApprove'], false)}
                className="h-4 w-4 text-violet-600 mt-1"
              />
              <div className="flex-1">
                <label htmlFor="deferApproval" className="text-sm text-foreground">
                  Defer to higher authority for approval
                </label>
                {settings.approverChangeBehavior?.autoApprove === false && (
                  <div className="mt-2">
                    <select
                      value={settings.approverChangeBehavior?.deferTo || 'admins'}
                      onChange={(e) => updateSetting(['approverChangeBehavior', 'deferTo'], e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                    >
                      <option value="line_manager">Line Manager</option>
                      <option value="admins">Admins</option>
                      <option value="head_of_org">Head of Organisation</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page approval removed - "Default Page Changes Requiring Approval" section removed */}

        {/* Tile Approver on Move */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-foreground mb-4">When Moving Tiles to Another Page</h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="changeToDefault"
                name="tileApproverOnMove"
                checked={settings.tileApproverOnMove === 'change_to_default'}
                onChange={() => updateSetting(['tileApproverOnMove'], 'change_to_default')}
                className="h-4 w-4 text-violet-600"
              />
              <label htmlFor="changeToDefault" className="text-sm text-foreground">
                Change to the new page's default approver
              </label>
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="keepOriginal"
                name="tileApproverOnMove"
                checked={settings.tileApproverOnMove === 'keep_original'}
                onChange={() => updateSetting(['tileApproverOnMove'], 'keep_original')}
                className="h-4 w-4 text-violet-600"
              />
              <label htmlFor="keepOriginal" className="text-sm text-foreground">
                Keep the original approver
              </label>
            </div>
          </div>
        </div>

        {/* Page Defaults Inheritance */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-foreground mb-1">Page Defaults Inheritance</h4>
              <p className="text-sm text-muted-foreground">
                Should page approvers inherit from parent page or use site defaults? (Used for tile approver defaults)
              </p>
            </div>
            <button
              onClick={() => updateSetting(['pageDefaultsInheritFromParent'], !settings.pageDefaultsInheritFromParent)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.pageDefaultsInheritFromParent ? 'bg-violet-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.pageDefaultsInheritFromParent ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {settings.pageDefaultsInheritFromParent
              ? 'Page approvers will inherit from parent page'
              : 'Page approvers will use site defaults'}
          </p>
        </div>

        {/* Validation Check Button */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">Validate Approvers</h4>
              <p className="text-sm text-blue-800">
                Check all tiles and pages to ensure they have valid approvers assigned
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  const response = await api.post('/approvals/validate');
                  if (response.data.data?.issues?.length > 0) {
                    toast.error(`Found ${response.data.data.issues.length} items without valid approvers`);
                  } else {
                    toast.success('All items have valid approvers');
                  }
                } catch (error: any) {
                  toast.error(`Validation failed: ${error.response?.data?.error || error.message}`);
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <CheckCircle2 className="h-4 w-4 inline mr-2" />
              Run Validation
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// Data Storage Settings Component
function DataStorageSettings({ userProfile, onRegisterHandlers }: { userProfile?: any; onRegisterHandlers?: (handlers: any) => void }) {
  const queryClient = useQueryClient();
  const [retentionValue, setRetentionValue] = useState(1);
  const [retentionUnit, setRetentionUnit] = useState('months');
  const [hasChanges, setHasChanges] = useState(false);

  // Get current user's organization ID from user profile
  const organizationId = userProfile?.organizationId;

  // Fetch current retention policy
  const { data: retentionPolicy, isLoading: isLoadingPolicy } = useQuery(
    ['organization-settings', organizationId, 'data_retention_policy'],
    async () => {
      try {
        const response = await api.get(`/settings/organization/${organizationId}/data_retention_policy`);
        return response.data;
      } catch (error: any) {
        // If 404, setting doesn't exist yet - return null to use defaults
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        if (data?.settingValue) {
          setRetentionValue(data.settingValue.value || 1);
          setRetentionUnit(data.settingValue.unit || 'months');
        } else {
          // Use defaults if no setting exists
          setRetentionValue(1);
          setRetentionUnit('months');
        }
      },
      retry: false, // Don't retry on 404 errors
      refetchOnWindowFocus: false,
    }
  );

  // Save retention policy mutation
  const saveRetentionMutation = useMutation(
    (policy: { value: number; unit: string }) =>
      api.put(`/settings/organization/${organizationId}/data_retention_policy`, {
        settingValue: policy
      }),
    {
      onSuccess: () => {
        setHasChanges(false);
        queryClient.invalidateQueries(['organization-settings', organizationId, 'data_retention_policy']);
        showAlert('Data retention policy saved successfully!');
      },
      onError: (error: any) => {
        showAlert(`Failed to save retention policy: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const handleRetentionValueChange = (value: string) => {
    const numValue = parseInt(value);
    if (numValue > 0) {
      setRetentionValue(numValue);
      setHasChanges(true);
    }
  };

  const handleRetentionUnitChange = (unit: string) => {
    setRetentionUnit(unit);
    setHasChanges(true);
  };

  const handleSave = useCallback(() => {
    saveRetentionMutation.mutate({ value: retentionValue, unit: retentionUnit });
  }, [saveRetentionMutation, retentionValue, retentionUnit]);

  const handleReset = useCallback(() => {
    if (retentionPolicy?.settingValue) {
      setRetentionValue(retentionPolicy.settingValue.value || 1);
      setRetentionUnit(retentionPolicy.settingValue.unit || 'months');
    } else {
      setRetentionValue(1);
      setRetentionUnit('months');
    }
    setHasChanges(false);
  }, [retentionPolicy]);

  // Store handlers in refs to avoid dependency issues
  const handleSaveRef = useRef(handleSave);
  const handleResetRef = useRef(handleReset);
  const prevValuesRef = useRef<{ hasChanges: boolean; isLoading: boolean } | null>(null);

  // Update refs when handlers change
  useEffect(() => {
    handleSaveRef.current = handleSave;
    handleResetRef.current = handleReset;
  }, [handleSave, handleReset]);

  // Register handlers with parent - only update when actual values change
  useEffect(() => {
    if (onRegisterHandlers && (
      prevValuesRef.current === null ||
      prevValuesRef.current.hasChanges !== hasChanges ||
      prevValuesRef.current.isLoading !== saveRetentionMutation.isLoading
    )) {
      prevValuesRef.current = { hasChanges, isLoading: saveRetentionMutation.isLoading };
      onRegisterHandlers({
        hasChanges,
        onSave: handleSaveRef.current,
        onReset: handleResetRef.current,
        isLoading: saveRetentionMutation.isLoading,
        saveLabel: 'Save Policy'
      });
    }
  }, [hasChanges, saveRetentionMutation.isLoading, onRegisterHandlers]);

  const getRetentionDescription = () => {
    const value = retentionValue;
    const unit = retentionUnit;
    const singular = value === 1;
    
    let unitDisplay = unit;
    if (singular) {
      unitDisplay = unit.slice(0, -1); // Remove 's' for singular
    }
    
    return `Deleted items will be permanently removed after ${value} ${unitDisplay}`;
  };

  const timeUnits = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
    { value: 'years', label: 'Years' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Data & Storage Settings</h3>
        <AIImportSettingsSection organizationId={organizationId} />
        <ImportHistoryAndContextSection organizationId={organizationId} />
        
        {/* Data Retention Policy */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mt-6">
          <div className="flex items-center space-x-3 mb-4">
            <Database className="h-5 w-5 text-teal-600" />
            <div>
              <h4 className="font-semibold text-foreground">Data Retention Policy</h4>
              <p className="text-sm text-muted-foreground">
                Configure how long deleted items are kept before permanent deletion
              </p>
            </div>
          </div>

          {isLoadingPolicy ? (
            <div className="text-sm text-muted-foreground">Loading current policy...</div>
          ) : (
            <div className="space-y-4">
              {/* Retention Period Setting */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Retention Period
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={retentionValue}
                    onChange={(e) => handleRetentionValueChange(e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <select
                    value={retentionUnit}
                    onChange={(e) => handleRetentionUnitChange(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {timeUnits.map(unit => (
                      <option key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {getRetentionDescription()}
                </p>
              </div>

              {/* Impact Information */}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <h5 className="text-sm font-medium text-amber-800 mb-1">Impact of Retention Policy</h5>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>• <strong>Shorter periods:</strong> Lower storage costs, risk of permanent data loss</li>
                  <li>• <strong>Longer periods:</strong> Higher storage costs, better data recovery options</li>
                  <li>• Items can be permanently deleted manually before the retention period expires</li>
                </ul>
              </div>

            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function AIImportSettingsSection({ organizationId }: { organizationId?: string }) {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<any | null>(null);
  const savedSnapshotRef = useRef<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: savedSettings, isLoading } = useQuery(
    ['organization-settings', organizationId, 'ai_import_settings'],
    () => api.get('/import-jobs/settings/effective').then((response) => response.data.data),
    {
      enabled: !!organizationId,
      retry: false,
      onSuccess: (data) => {
        savedSnapshotRef.current = JSON.stringify(data);
        setSettings(data);
      },
    }
  );

  const saveMutation = useMutation(
    (nextSettings: any) => api.put(`/settings/organization/${organizationId}/ai_import_settings`, { settingValue: nextSettings }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization-settings', organizationId, 'ai_import_settings']);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Failed to save AI import settings.');
      },
    }
  );

  useEffect(() => {
    if (!savedSettings) return;
    savedSnapshotRef.current = JSON.stringify(savedSettings);
    setSettings(savedSettings);
  }, [savedSettings]);

  useEffect(() => {
    if (!organizationId || !settings) return;
    const serialized = JSON.stringify(settings);
    if (!savedSnapshotRef.current) {
      savedSnapshotRef.current = serialized;
      return;
    }
    if (serialized === savedSnapshotRef.current) return;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      saveMutation.mutate(settings, {
        onSuccess: () => {
          savedSnapshotRef.current = serialized;
        },
      });
    }, 500);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [organizationId, settings]);

  const updatePolicy = (key: string, value: string) => {
    setSettings((current: any) => ({
      ...current,
      structuralCreation: {
        ...(current?.structuralCreation || {}),
        [key]: value,
      },
    }));
  };

  const updateThreshold = (key: string, value: number) => {
    setSettings((current: any) => ({
      ...current,
      confidenceThresholds: {
        ...(current?.confidenceThresholds || {}),
        [key]: value / 100,
      },
    }));
  };

  if (!organizationId) return null;

  const policies = [
    { key: 'nestedPages', label: 'New nested pages', description: 'When a new page is the best destination, allow Nexus to place it beneath the most relevant existing page at any depth.' },
    { key: 'columns', label: 'New page sections', description: 'When the destination page lacks a suitable section, allow Nexus to suggest a new column category.' },
  ];
  const creationPolicyOptions = [
    { value: 'never', label: "Don't allow" },
    { value: 'review_required', label: 'Ask before creating' },
    { value: 'always', label: 'Allowed without extra approval' },
  ];
  const getOptionLabel = (options: Array<{ value: string; label: string }>, value: string) => (
    options.find((option) => option.value === value)?.label || value
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Zap className="h-5 w-5 text-blue-600" />
          <div>
            <h4 className="font-semibold text-foreground">Document import defaults</h4>
            <p className="text-sm text-muted-foreground">Organisation-wide boundaries for every document import.</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">
          {saveMutation.isLoading ? 'Saving...' : saveMutation.isError ? 'Save failed' : 'Autosaves changes'}
        </span>
      </div>

      {isLoading || !settings ? (
        <p className="text-sm text-muted-foreground">Loading AI import settings...</p>
      ) : (
        <div className="space-y-6">
          <div className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
            <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              These rules set the maximum freedom Nexus has. The import wizard can ask for more review, but it cannot bypass a <strong>Don&apos;t allow</strong> rule.
            </p>
          </div>

          <section className="space-y-3">
            <div>
              <h5 className="text-sm font-semibold text-gray-900">What structure may Nexus add?</h5>
              <p className="mt-0.5 text-xs text-gray-500">Control when the importer may extend your page structure to create a suitable destination.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {policies.map(({ key, label, description }) => (
              <div key={key} className="rounded-md border border-gray-200 p-3">
                <span className="block text-sm font-medium text-gray-800">{label}</span>
                <span className="mb-2 mt-1 block min-h-10 text-xs leading-5 text-gray-500">{description}</span>
                <Select
                  value={settings.structuralCreation?.[key] || 'review_required'}
                  onValueChange={(value) => updatePolicy(key, value)}
                >
                  <SelectTrigger className="h-9 px-2 py-2 text-sm">
                    <span>{getOptionLabel(creationPolicyOptions, settings.structuralCreation?.[key] || 'review_required')}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {creationPolicyOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            </div>
          </section>

          <section className="space-y-3 border-t border-gray-200 pt-5">
            <div>
              <h5 className="text-sm font-semibold text-gray-900">When is Nexus confident enough?</h5>
              <p className="mt-0.5 text-xs text-gray-500">These percentages change when an item is flagged or when new structure may be suggested.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                { key: 'placement', label: 'Destination confidence', description: 'Below this score, the destination is treated as uncertain and the item is sent to review or the Knowledge Inbox.', direction: 'Higher means more items are treated as uncertain.' },
                { key: 'duplicate', label: 'Possible duplicate match', description: 'At or above this score, an existing item is treated as a possible duplicate and is flagged for review.', direction: 'Lower means more matches are flagged.' },
                { key: 'structure', label: 'New section confidence', description: 'Nexus only suggests a new page section when its destination confidence reaches this score.', direction: 'Higher means fewer new sections are suggested.' },
              ].map(({ key, label, description, direction }) => (
                <label key={key} className="rounded-md border border-gray-200 p-3">
                  <span className="block text-sm font-medium text-gray-800">{label}</span>
                  <span className="mt-1 block min-h-[60px] text-xs leading-5 text-gray-500">{description}</span>
                  <span className="relative mt-2 block">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={Math.round((settings.confidenceThresholds?.[key] || 0.8) * 100)}
                      onChange={(event) => updateThreshold(key, Math.min(100, Math.max(1, Number(event.target.value) || 1)))}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 pr-8 text-sm"
                    />
                    <span className="pointer-events-none absolute right-3 top-2 text-sm text-gray-500">%</span>
                  </span>
                  <span className="mt-1.5 block text-[11px] leading-4 text-gray-500">{direction}</span>
                </label>
              ))}
            </div>
          </section>

          <p className="border-t border-gray-200 pt-4 text-xs leading-5 text-gray-500">
            Possible duplicates are always surfaced in the import plan once they meet the duplicate-match score. You decide whether to include or skip them before the import runs.
          </p>
        </div>
      )}
    </div>
  );
}

function ImportHistoryAndContextSection({ organizationId }: { organizationId?: string }) {
  const queryClient = useQueryClient();
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);
  const [selectedImportId, setSelectedImportId] = useState<string | null>(null);
  const [newFact, setNewFact] = useState({
    factType: 'jurisdiction',
    canonicalKey: 'organization.jurisdiction',
    displayValue: '',
    sensitivity: 'internal',
    visibility: 'organization',
    applicability: '',
    effectiveFrom: '',
    effectiveTo: '',
  });
  const [editingFact, setEditingFact] = useState<null | {
    id: string;
    displayValue: string;
    applicability: string;
    effectiveFrom: string;
    effectiveTo: string;
    sensitivity: string;
    visibility: string;
    value: Record<string, unknown>;
  }>(null);
  const parseFactApplicability = (value: string): Record<string, unknown> => {
    const trimmed = value.trim();
    if (!trimmed) return {};
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
      } catch {
        // Fall back to a simple scope list so an invalid JSON fragment is not lost.
      }
    }
    return { scope: trimmed.split(',').map((item) => item.trim()).filter(Boolean) };
  };
  const { data: history } = useQuery(
    ['import-history', organizationId],
    () => api.get('/import-jobs/history').then(res => res.data.data),
    { enabled: !!organizationId }
  );
  const { data: selectedImport, isLoading: selectedImportLoading } = useQuery(
    ['import-summary', selectedImportId],
    () => api.get(`/import-jobs/${selectedImportId}/summary`).then(res => res.data.data),
    { enabled: !!selectedImportId }
  );
  const { data: context } = useQuery(
    ['organization-context', organizationId],
    () => api.get('/organisation-facts').then(res => res.data.data),
    { enabled: !!organizationId }
  );

  const rebuildMutation = useMutation(
    () => api.post('/organisation-facts/rebuild'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization-context', organizationId]);
        toast.success('Organisation context rebuilt.');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Context rebuild failed.');
      },
    }
  );

  const createFactMutation = useMutation(
    () => api.post('/organisation-facts/facts', {
      namespace: 'organization',
      factType: newFact.factType,
      canonicalKey: newFact.canonicalKey,
      value: { text: newFact.displayValue },
      displayValue: newFact.displayValue,
      applicability: parseFactApplicability(newFact.applicability),
      sourceType: 'administrator',
      provenance: { label: 'Organisation settings' },
      status: 'verified',
      confidence: 100,
      sensitivity: newFact.sensitivity,
      visibility: newFact.visibility,
      effectiveFrom: newFact.effectiveFrom || null,
      effectiveTo: newFact.effectiveTo || null,
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization-context', organizationId]);
        setNewFact((current) => ({ ...current, displayValue: '', applicability: '', effectiveFrom: '', effectiveTo: '' }));
        toast.success('Organisation fact saved.');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Could not save organisation fact.');
      },
    }
  );

  const updateFactMutation = useMutation(
    ({ factId, patch }: { factId: string; patch: Record<string, unknown> }) =>
      api.patch(`/organisation-facts/facts/${factId}`, patch),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization-context', organizationId]);
        toast.success('Organisation fact updated.');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Could not update organisation fact.');
      },
    }
  );

  const resolveFactConflictMutation = useMutation(
    (factId: string) => api.post(`/organisation-facts/facts/${factId}/resolve-conflict`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization-context', organizationId]);
        toast.success('Fact conflict resolved. Superseded values remain in the audit history.');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Could not resolve the fact conflict.');
      },
    }
  );

  const rollbackMutation = useMutation(
    (jobId: string) => api.post(`/import-jobs/${jobId}/rollback`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['import-history', organizationId]);
        queryClient.invalidateQueries(['organization-context', organizationId]);
        queryClient.invalidateQueries(['page-init']);
        queryClient.invalidateQueries('all-pages');
        toast.success('Import rolled back.');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Rollback failed.');
      },
    }
  );

  const canRollbackImport = (job: any) => ['completed', 'partial'].includes(job.status) && job.rollbackStatus !== 'completed';
  const selectedJob = (history || []).find((job: any) => job.id === selectedImportId);
  const conflictingFactIds = new Set<string>(
    (context?.factConflicts || []).flatMap((conflict: any) => conflict.factIds || []),
  );
  const factUsageLabel = (intent: string) => ({
    agent_query: 'organisation agent', document_create: 'document creation', document_review: 'document review',
    document_modify: 'document changes', missing_document_create: 'missing-document creation',
    import_analysis: 'import analysis', import_placement: 'import placement', search: 'search interpretation',
    change_validation: 'change validation', workflow_design: 'workflow design', form_design: 'form design',
    register_design: 'register design', automation: 'automations',
  } as Record<string, string>)[intent] || intent;
  const navigateToImportedTile = (tile: any) => {
    if (!tile?.pageId || !tile?.tileId || tile.currentStatus !== 'available') return;
    window.location.href = `/page/${tile.pageId}?highlight=${tile.tileId}`;
  };

  return (
    <>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Receipt className="h-5 w-5 text-teal-600" />
          <div className="min-w-0 flex-1">
            <h4 className="font-semibold text-foreground">Import History</h4>
            <p className="text-sm text-muted-foreground">Recent site-wide document import jobs and outcomes.</p>
          </div>
          <button
            onClick={() => setIsImportWizardOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
          >
            <Upload className="h-4 w-4" />
            <span>Import documents</span>
          </button>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {(history || []).length === 0 && <p className="text-sm text-muted-foreground">No imports yet.</p>}
          {(history || []).map((job: any) => (
            <div
              key={job.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedImportId(job.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedImportId(job.id);
                }
              }}
              className={`cursor-pointer border rounded-md px-3 py-2 transition-colors ${
                selectedImportId === job.id ? 'border-teal-300 bg-teal-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-900">{new Date(job.createdAt).toLocaleString()}</p>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{job.status}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-gray-500">
                  {job.documentCount} docs, {job.successCount} succeeded, {job.failureCount} failed, mode {job.mode}
                </p>
                {canRollbackImport(job) ? (
                  <button
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (await showConfirm('Roll back the pages and tiles created by this import?')) {
                        rollbackMutation.mutate(job.id);
                      }
                    }}
                    disabled={rollbackMutation.isLoading}
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    <RotateCcw className="h-3 w-3" />
                    <span>{rollbackMutation.isLoading ? 'Rolling back...' : 'Rollback'}</span>
                  </button>
                ) : job.rollbackStatus === 'completed' || job.status === 'rolled_back' ? (
                  <span className="text-xs text-gray-500">Rolled back</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        {selectedImportId && (
          <div className="mt-4 rounded-lg border border-teal-200 bg-teal-50/50 p-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h5 className="text-sm font-semibold text-gray-900">Imported Documents</h5>
                <p className="text-xs text-gray-600">
                  {selectedJob ? new Date(selectedJob.createdAt).toLocaleString() : 'Selected import'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedImportId(null)}
                className="rounded-md p-1 text-gray-500 hover:bg-white hover:text-gray-700"
                aria-label="Close import details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {selectedImportLoading ? (
              <p className="text-sm text-gray-600">Loading imported documents...</p>
            ) : (
              <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                {(selectedImport?.importedDocuments || []).length === 0 && (
                  <p className="text-sm text-gray-600">No document-level import records were found for this import.</p>
                )}
                {(selectedImport?.importedDocuments || []).map((document: any) => (
                  <div key={document.documentId} className="rounded-md border border-gray-200 bg-white p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{document.detectedTitle || document.fileName}</p>
                        <p className="truncate text-xs text-gray-500">{document.fileName}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">{document.status}</span>
                    </div>
                    {document.error && <p className="mt-2 text-xs text-red-600">{document.error}</p>}
                    <div className="mt-3 space-y-2">
                      {(document.createdTiles || []).length === 0 ? (
                        <p className="text-xs text-gray-500">No tile was created for this document.</p>
                      ) : (
                        document.createdTiles.map((tile: any) => (
                          <button
                            key={tile.tileId}
                            type="button"
                            onClick={() => navigateToImportedTile(tile)}
                            disabled={tile.currentStatus !== 'available'}
                            className="flex w-full items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 text-left text-xs hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <span className="min-w-0">
                              <span className="block truncate font-medium text-gray-800">{tile.title}</span>
                              <span className="block truncate text-gray-500">
                                {(tile.pagePath || [tile.pageTitle]).join(' > ')}
                                {tile.position ? `, row ${tile.position.row}, column ${tile.position.column}` : ''}
                              </span>
                              {tile.unavailableReason && (
                                <span className="block text-red-600">{tile.unavailableReason}</span>
                              )}
                            </span>
                            {tile.currentStatus === 'available' && <ExternalLink className="h-4 w-4 shrink-0 text-teal-600" />}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Eye className="h-5 w-5 text-indigo-600" />
            <div>
              <h4 className="font-semibold text-foreground">Organisation Context</h4>
              <p className="text-sm text-muted-foreground">Verified facts and permission-aware semantic knowledge used by Nexus AI.</p>
            </div>
          </div>
          {context?.canManage && (
            <button
              onClick={() => rebuildMutation.mutate()}
              disabled={rebuildMutation.isLoading}
              className="inline-flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${rebuildMutation.isLoading ? 'animate-spin' : ''}`} />
              <span>Rebuild</span>
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-md bg-gray-50 p-3">
            <p className="text-lg font-semibold text-gray-900">{context?.factSummary?.verified || 0}</p>
            <p className="text-xs text-gray-500">Verified facts</p>
          </div>
          <div className="rounded-md bg-gray-50 p-3">
            <p className="text-lg font-semibold text-gray-900">{context?.factSummary?.proposed || 0}</p>
            <p className="text-xs text-gray-500">Awaiting review</p>
          </div>
          <div className="rounded-md bg-gray-50 p-3">
            <p className="text-lg font-semibold text-gray-900">
              {(context?.projectionHealth?.activePages || 0) +
                (context?.projectionHealth?.activeTiles || 0) +
                (context?.projectionHealth?.activeForms || 0) +
                (context?.projectionHealth?.activeRegisters || 0) +
                (context?.projectionHealth?.activeWorkflows || 0)}
            </p>
            <p className="text-xs text-gray-500">Knowledge units</p>
          </div>
        </div>

        {context?.jurisdiction?.requiresClarification && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Confirm the organisation jurisdiction before generating regulated policies or forms.</span>
            </div>
          </div>
        )}

        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {(context?.facts || []).length === 0 && (
            <p className="text-sm text-gray-500">No organisation facts have been recorded yet.</p>
          )}
          {(context?.facts || []).map((fact: any, index: number, allFacts: any[]) => (
            <React.Fragment key={fact.id}>
            {(index === 0 || allFacts[index - 1]?.namespace !== fact.namespace) && (
              <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{fact.namespace}</p>
            )}
            <div className={`rounded-md border p-3 ${conflictingFactIds.has(fact.id) ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{fact.canonicalKey}</p>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${
                      fact.status === 'verified'
                        ? 'bg-emerald-50 text-emerald-700'
                        : fact.status === 'disputed'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-amber-50 text-amber-700'
                    }`}>{fact.status}</span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{fact.sensitivity}</span>
                    {conflictingFactIds.has(fact.id) && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">conflict</span>
                    )}
                  </div>
                  {editingFact?.id === fact.id ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={editingFact.displayValue}
                        onChange={(event) => setEditingFact((current) => current ? { ...current, displayValue: event.target.value } : current)}
                        rows={3}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      />
                      <input
                        value={editingFact.applicability}
                        onChange={(event) => setEditingFact((current) => current ? { ...current, applicability: event.target.value } : current)}
                        placeholder='Applicability JSON, e.g. {"workerGroups":["employees"]}'
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input type="date" value={editingFact.effectiveFrom} onChange={(event) => setEditingFact((current) => current ? { ...current, effectiveFrom: event.target.value } : current)} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" />
                        <input type="date" value={editingFact.effectiveTo} onChange={(event) => setEditingFact((current) => current ? { ...current, effectiveTo: event.target.value } : current)} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" />
                        <select value={editingFact.sensitivity} onChange={(event) => setEditingFact((current) => current ? { ...current, sensitivity: event.target.value } : current)} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs">
                          <option value="public">Public</option><option value="internal">Internal</option><option value="confidential">Confidential</option><option value="restricted">Restricted</option>
                        </select>
                        <select value={editingFact.visibility} onChange={(event) => setEditingFact((current) => current ? { ...current, visibility: event.target.value } : current)} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs">
                          <option value="organization">Organisation AI</option><option value="administrators">Administrators only</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => {
                          updateFactMutation.mutate({ factId: fact.id, patch: {
                            displayValue: editingFact.displayValue,
                            value: { ...editingFact.value, text: editingFact.displayValue },
                            applicability: parseFactApplicability(editingFact.applicability),
                            effectiveFrom: editingFact.effectiveFrom || null,
                            effectiveTo: editingFact.effectiveTo || null,
                            sensitivity: editingFact.sensitivity,
                            visibility: editingFact.visibility,
                          } });
                          setEditingFact(null);
                        }} className="rounded-md bg-indigo-600 px-2 py-1 text-xs text-white">Save</button>
                        <button type="button" onClick={() => setEditingFact(null)} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-gray-700">{fact.displayValue}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Source: {fact.sourceType}
                    {fact.verifiedAt ? ` · verified ${new Date(fact.verifiedAt).toLocaleDateString()}` : ''}
                    {fact.effectiveTo ? ` · effective until ${new Date(fact.effectiveTo).toLocaleDateString()}` : ''}
                  </p>
                  {Object.keys(fact.applicability || {}).length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">Applies to: {JSON.stringify(fact.applicability)}</p>
                  )}
                  {(context?.factUsage?.[fact.id] || []).length > 0 && (
                    <p className="mt-1 text-xs text-indigo-700">
                      Used by: {(context.factUsage[fact.id] || []).map(factUsageLabel).join(', ')}
                    </p>
                  )}
                </div>
                {context?.canManage && fact.status === 'proposed' && (
                  <button
                    type="button"
                    onClick={() => updateFactMutation.mutate({ factId: fact.id, patch: { status: 'verified', confidence: 100 } })}
                    disabled={updateFactMutation.isLoading}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <Check className="h-3 w-3" />
                    Verify
                  </button>
                )}
              </div>
              {context?.canManage && ['verified', 'proposed', 'disputed'].includes(fact.status) && (
                <div className="mt-2 flex flex-wrap gap-3">
                  <button type="button" onClick={() => setEditingFact({
                    id: fact.id,
                    displayValue: fact.displayValue,
                    applicability: Object.keys(fact.applicability || {}).length ? JSON.stringify(fact.applicability) : '',
                    effectiveFrom: fact.effectiveFrom ? String(fact.effectiveFrom).slice(0, 10) : '',
                    effectiveTo: fact.effectiveTo ? String(fact.effectiveTo).slice(0, 10) : '',
                    sensitivity: fact.sensitivity,
                    visibility: fact.visibility,
                    value: fact.value || {},
                  })} className="text-xs text-indigo-600 hover:text-indigo-800">Edit</button>
                  {conflictingFactIds.has(fact.id) && fact.status === 'verified' && (
                    <button type="button" onClick={async () => {
                      if (await showConfirm('Keep this fact and supersede the conflicting verified values?')) {
                        resolveFactConflictMutation.mutate(fact.id);
                      }
                    }} className="text-xs font-medium text-red-600 hover:text-red-800">Keep this and resolve conflict</button>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      if (await showConfirm('Supersede this organisation fact? It will remain in the audit history.')) {
                        updateFactMutation.mutate({ factId: fact.id, patch: { status: 'superseded' } });
                      }
                    }}
                    className="text-xs text-gray-500 hover:text-red-600"
                  >
                    Supersede
                  </button>
                </div>
              )}
            </div>
            </React.Fragment>
          ))}
        </div>

        {context?.canManage && (
          <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
            <p className="mb-2 text-sm font-medium text-gray-900">Add a verified fact</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input
                value={newFact.factType}
                onChange={(event) => setNewFact((current) => ({ ...current, factType: event.target.value }))}
                placeholder="Fact type, e.g. jurisdiction"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                value={newFact.canonicalKey}
                onChange={(event) => setNewFact((current) => ({ ...current, canonicalKey: event.target.value }))}
                placeholder="Canonical key"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <textarea
                value={newFact.displayValue}
                onChange={(event) => setNewFact((current) => ({ ...current, displayValue: event.target.value }))}
                placeholder="Fact value and any important scope"
                rows={3}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm sm:col-span-2"
              />
              <input
                value={newFact.applicability}
                onChange={(event) => setNewFact((current) => ({ ...current, applicability: event.target.value }))}
                placeholder='Applicability, e.g. {"workerGroups":["employees"],"locations":["NSW"]}'
                className="rounded-md border border-gray-300 px-3 py-2 text-sm sm:col-span-2"
              />
              <label className="text-xs text-gray-600">
                Effective from
                <input type="date" value={newFact.effectiveFrom} onChange={(event) => setNewFact((current) => ({ ...current, effectiveFrom: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </label>
              <label className="text-xs text-gray-600">
                Effective to
                <input type="date" value={newFact.effectiveTo} onChange={(event) => setNewFact((current) => ({ ...current, effectiveTo: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              </label>
              <select
                value={newFact.sensitivity}
                onChange={(event) => setNewFact((current) => ({ ...current, sensitivity: event.target.value }))}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
              </select>
              <select
                value={newFact.visibility}
                onChange={(event) => setNewFact((current) => ({ ...current, visibility: event.target.value }))}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="organization">Available to authorised organisation AI</option>
                <option value="administrators">Administrators only</option>
              </select>
            </div>
            <button
              type="button"
              onClick={() => createFactMutation.mutate()}
              disabled={createFactMutation.isLoading || newFact.displayValue.trim().length < 2}
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Save verified fact
            </button>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-gray-600">
          <span>Projection status: {(context?.projectionHealth?.staleProjectionUnits || 0) === 0 ? 'Current' : `${context.projectionHealth.staleProjectionUnits} stale`}</span>
          <span>Latest projected change: {context?.projectionHealth?.lastProjectedAt ? new Date(context.projectionHealth.lastProjectedAt).toLocaleString() : 'Not yet'}</span>
          <span>Ambiguous placements: {context?.projectionHealth?.ambiguousPlacementTiles || 0}</span>
          <span>Invalid form references: {context?.projectionHealth?.invalidFormReferences || 0}</span>
          <span>Hierarchy cycles: {context?.projectionHealth?.hierarchyCycles || 0}</span>
          <span>Broken nested links: {context?.projectionHealth?.nestedPageLinkMismatches || 0}</span>
        </div>
      </div>
    </div>
    <DocumentImportWizard
      isOpen={isImportWizardOpen}
      onClose={() => setIsImportWizardOpen(false)}
    />
    </>
  );
}

// Delete Account Section Component
interface DeleteAccountSectionProps {
  onClose: () => void;
}

function DeleteAccountSection({ onClose }: DeleteAccountSectionProps) {
  const [showFirstWarning, setShowFirstWarning] = useState(false);
  const [showSecondWarning, setShowSecondWarning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const { signOut } = useClerk();

  const removeUserMutation = useMutation({
    mutationFn: (reason?: string) => api.delete('/organizations/leave', { data: { reason } }),
    onSuccess: async () => {
      // User has been removed, close modal and sign out
      onClose();
      showAlert('You have been removed from the organization. You will now be signed out.');
      
      try {
        // Use centralized logout handler
        await handleLogout(signOut)();
      } catch (error) {
        console.error('Error signing out:', error);
        // Fallback: force page reload to trigger auth state update
        window.location.reload();
      }
    },
    onError: (error: any) => {
      console.error('Failed to remove user:', error);
      showAlert(error.response?.data?.error || 'Failed to remove account from organization');
      setIsRemoving(false);
      setShowFirstWarning(false);
      setShowSecondWarning(false);
    }
  });

  const handleFirstConfirm = () => {
    setShowFirstWarning(false);
    setShowSecondWarning(true);
  };

  const handleFinalConfirm = () => {
    setIsRemoving(true);
    removeUserMutation.mutate('User requested account deletion');
  };

  const handleCancel = () => {
    setShowFirstWarning(false);
    setShowSecondWarning(false);
  };

  return (
    <>
      <div className="bg-white border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Trash2 className="h-5 w-5 text-red-600" />
          <div>
            <h4 className="font-semibold text-foreground">Delete Account</h4>
            <p className="text-sm text-muted-foreground">Remove your account from this organization</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Warning: This action cannot be undone</p>
                <p className="text-xs text-red-700 mt-1">
                  Removing your account will permanently delete your access to this organization. 
                  You will lose access to all pages, forms, and data within this organization.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowFirstWarning(true)}
            disabled={isRemoving}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRemoving ? 'Removing Account...' : 'Delete My Account'}
          </button>
        </div>
      </div>

      {/* First Warning Modal */}
      {showFirstWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900">Confirm Account Deletion</h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete your account from this organization? This will:
            </p>
            
            <ul className="text-sm text-gray-600 mb-6 space-y-1 ml-4">
              <li>• Remove your access to all organization pages and data</li>
              <li>• Delete your profile from this organization</li>
              <li>• Remove you from any assigned roles or permissions</li>
              <li>• Log you out of Nexus</li>
            </ul>
            
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFirstConfirm}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Second Warning Modal */}
      {showSecondWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900">Final Confirmation</h3>
            </div>
            
            <p className="text-gray-700 mb-4">
              <strong>This is your final warning.</strong> Once you confirm, your account will be immediately removed from this organization.
            </p>
            
            <p className="text-red-700 font-medium mb-6">
              This action cannot be undone. You will need to be re-invited to regain access.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                disabled={isRemoving}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalConfirm}
                disabled={isRemoving}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isRemoving ? 'Removing...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Apply Template Section Component
function ApplyTemplateSection({ queryClient }: { queryClient: any }) {
  const [selectedSharedId, setSelectedSharedId] = useState<string>('');
  const [templateMode, setTemplateMode] = useState<'homepage' | 'nested'>('nested');
  const [isApplying, setIsApplying] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);

  // Fetch available shared templates (only templates, not welcome pages)
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get('/shared-pages?templates=true');
        if (res.data?.success && res.data.data?.length > 0) {
          setAvailableTemplates(res.data.data);
          if (!selectedSharedId && res.data.data.length > 0) {
            // Auto-select first template
            setSelectedSharedId(res.data.data[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch shared templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  const handleApplyTemplate = async () => {
    if (!selectedSharedId) {
      toast.error('Please select a template to apply.');
      return;
    }

    setIsApplying(true);
    try {
      const res = await api.post('/organizations/apply-template', {
        sharedId: selectedSharedId,
        mode: templateMode,
      });

      if (res.data?.success) {
        toast.success(res.data.data?.message || 'Template applied successfully!');
        // Invalidate queries to refresh pages
        queryClient.invalidateQueries(['page']);
        queryClient.invalidateQueries(['pages']);
        queryClient.invalidateQueries(['all-pages']);
        // Reload to show the new template
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error('Failed to apply template');
      }
    } catch (e: any) {
      console.error('Apply template failed', e);
      toast.error(e?.response?.data?.error || 'Failed to apply template');
    } finally {
      setIsApplying(false);
    }
  };

  if (availableTemplates.length === 0) {
    return null; // Don't show section if no templates available
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-4">
        <Copy className="h-5 w-5 text-blue-600" />
        <div>
          <h4 className="font-semibold text-blue-900">Apply Nexus Template</h4>
          <p className="text-sm text-blue-800">
            Clone a template organizational structure to your organization.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Template Selection */}
        <div>
          <label className="block text-sm font-medium text-blue-900 mb-2">
            Select Template
          </label>
          <select
            value={selectedSharedId}
            onChange={(e) => setSelectedSharedId(e.target.value)}
            className="w-full px-3 py-2 border border-blue-300 rounded-md bg-white text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isApplying}
          >
            {availableTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.label}
              </option>
            ))}
          </select>
        </div>

        {/* Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-blue-900 mb-2">
            Apply As
          </label>
          <div className="space-y-2">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="templateMode"
                value="nested"
                checked={templateMode === 'nested'}
                onChange={(e) => setTemplateMode(e.target.value as 'nested')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-blue-300"
                disabled={isApplying}
              />
              <div className="flex-1">
                <span className="font-medium text-blue-900">Nested Page</span>
                <p className="text-xs text-blue-700 mt-1">
                  Create the template as a nested page under your current homepage
                </p>
              </div>
            </label>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="templateMode"
                value="homepage"
                checked={templateMode === 'homepage'}
                onChange={(e) => setTemplateMode(e.target.value as 'homepage')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-blue-300"
                disabled={isApplying}
              />
              <div className="flex-1">
                <span className="font-medium text-blue-900">Homepage</span>
                <p className="text-xs text-blue-700 mt-1">
                  Replace your current homepage with the template (previous homepage becomes nested)
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Warning */}
        <div className="p-3 bg-blue-100 border border-blue-300 rounded-md">
          <p className="text-xs text-blue-800">
            <strong>Note:</strong> This creates a copy of the template in your organization. You can edit, delete, or modify the cloned pages as needed.
          </p>
        </div>

        {/* Apply Button */}
        <button
          onClick={handleApplyTemplate}
          disabled={isApplying || !selectedSharedId}
          className="w-full px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isApplying ? 'Applying Template...' : 'Apply Template'}
        </button>
      </div>
    </div>
  );
}

// Main export component - context now provided at Layout level
export default function SiteSettingsModal({ isOpen, onClose }: SiteSettingsModalProps) {
  return <SiteSettingsModalContent isOpen={isOpen} onClose={onClose} />;
}
